// src/services/archivos.ts
import { api, getAccessToken } from './api';
import type { Archivo, ArchivoContexto, ArchivoListResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const archivosService = {
  listar: (contexto: ArchivoContexto, entidadId: string) =>
    api.get<ArchivoListResponse>(`/archivos/entidad/${contexto}/${entidadId}`),

  subir: async (
    contexto: ArchivoContexto,
    entidadId: string,
    file: File,
    descripcion?: string,
  ): Promise<{ archivo: Archivo; mensaje: string }> => {
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('contexto', contexto);
    formData.append('entidad_id', entidadId);
    if (descripcion) formData.append('descripcion', descripcion);

    const response = await fetch(`${API_BASE_URL}/archivos/subir`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Error al subir archivo' }));
      throw new Error(error.detail || `Error ${response.status}`);
    }

    return response.json();
  },

  subirMultiples: async (
    contexto: ArchivoContexto,
    entidadId: string,
    files: File[],
  ): Promise<{ exitosos: Archivo[]; fallidos: { nombre: string; error: string }[]; total_subidos: number; total_fallidos: number }> => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('archivos', file);
    }
    formData.append('contexto', contexto);
    formData.append('entidad_id', entidadId);

    const response = await fetch(`${API_BASE_URL}/archivos/subir-multiples`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Error al subir archivos' }));
      throw new Error(error.detail || `Error ${response.status}`);
    }

    return response.json();
  },

  descargar: async (archivoId: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/archivos/${archivoId}/descargar`, {
      headers: authHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Error al descargar archivo' }));
      throw new Error(error.detail || `Error ${response.status}`);
    }

    return response.blob();
  },

  miniatura: async (archivoId: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/archivos/${archivoId}/miniatura`, {
      headers: authHeaders(),
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to load thumbnail');

    return response.blob();
  },

  eliminar: (archivoId: string) =>
    api.delete<void>(`/archivos/${archivoId}`),
};
